/* ===========================================================================
   Kitsters - whole-card click
   ---------------------------------------------------------------------------
   Makes the ENTIRE product card open the product, not just its image/title.

   Opt-in per card: the card root carries `data-card-link="<product url>"`.
   Nothing else about the card changes - the existing image link, title link,
   collection link, wishlist heart and Add-to-Cart button keep working exactly
   as they do today. This handler only fires on the card's "dead space"
   (padding, chips, price row, trust line, background), where a click does
   nothing at all right now.

   Design rules (deliberately defensive - this runs on the live storefront):
   - Bail on ANY interactive ancestor between the click and the card. A click
     inside a link, button or form control is that element's click, never the
     card's, so nothing can be hijacked.
   - Bail if the click ended a text selection (a shopper highlighting a price
     should not be navigated away).
   - Bail if the pointer moved between press and release - that is a drag on a
     carousel/scroller, not a tap.
   - Bail if another handler already called preventDefault().
   - Ctrl/Cmd/Shift-click and middle-click open a new tab, matching a real link.
   - Any unexpected state returns silently, so the worst case is simply the
     behaviour we had before this file existed.
   =========================================================================== */
(function () {
  'use strict';

  var CARD = '[data-card-link]';

  /* Everything a shopper can already act on. Kept as one string so the whole
     exclusion list is visible in one place - add to it, never subtract. */
  var INTERACTIVE = [
    'a[href]',
    'button',
    'input',
    'select',
    'textarea',
    'label',
    'summary',
    'details',
    'option',
    'video',
    'audio',
    'iframe',
    'form',
    '[role="button"]',
    '[role="link"]',
    '[role="tab"]',
    '[role="checkbox"]',
    '[role="radio"]',
    '[onclick]',
    '[contenteditable]',
    '[data-add-to-cart]',
    '[data-wishlist]',
    '[data-quick-view]',
    '[data-no-card-link]',
    /* Dawn + Kitsters interactive elements (India's build lists the Kalles
       equivalents here; this tree is Dawn-only). */
    'kitsters-wishlist',
    'modal-opener',
    'quick-add-modal',
    'product-form',
    'variant-selects',
    'variant-radios'
  ].join(',');

  /* ── Drag guard ─────────────────────────────────────────────────────────
     Carousels (PDP "Pairs well with", supplies rails) are drag-scrollable;
     a drag that happens to end over a card must not navigate. */
  var downX = 0;
  var downY = 0;
  var dragged = false;
  var DRAG_SLOP = 10; /* px */

  function onDown(x, y) {
    downX = x;
    downY = y;
    dragged = false;
  }

  function onUp(x, y) {
    dragged = Math.abs(x - downX) > DRAG_SLOP || Math.abs(y - downY) > DRAG_SLOP;
  }

  if (window.PointerEvent) {
    document.addEventListener('pointerdown', function (e) { onDown(e.clientX, e.clientY); }, true);
    document.addEventListener('pointerup', function (e) { onUp(e.clientX, e.clientY); }, true);
  } else {
    document.addEventListener('mousedown', function (e) { onDown(e.clientX, e.clientY); }, true);
    document.addEventListener('mouseup', function (e) { onUp(e.clientX, e.clientY); }, true);
    document.addEventListener('touchstart', function (e) {
      var t = e.changedTouches && e.changedTouches[0];
      if (t) onDown(t.clientX, t.clientY);
    }, true);
    document.addEventListener('touchend', function (e) {
      var t = e.changedTouches && e.changedTouches[0];
      if (t) onUp(t.clientX, t.clientY);
    }, true);
  }

  /* ── Resolution ─────────────────────────────────────────────────────────── */

  /* Returns the card this click should open, or null if it should be ignored. */
  function cardFor(target) {
    if (!target || typeof target.closest !== 'function') return null;

    var card = target.closest(CARD);
    if (!card) return null;

    /* An interactive element sitting between the click and the card wins.
       `card.contains` guards the (impossible-today, cheap-tomorrow) case of a
       card nested inside a link. */
    var hit = target.closest(INTERACTIVE);
    if (hit && card.contains(hit) && hit !== card) return null;

    return card;
  }

  function urlFor(card) {
    var url = card.getAttribute('data-card-link');
    return url && url.length ? url : null;
  }

  /* A click that finishes a text selection is a selection, not a navigation. */
  function isSelectingText() {
    try {
      var sel = window.getSelection();
      return !!sel && !sel.isCollapsed && sel.toString().length > 0;
    } catch (err) {
      return false;
    }
  }

  function openInNewTab(url) {
    var win = window.open(url, '_blank');
    if (win) win.opener = null;
  }

  /* ── Handlers ───────────────────────────────────────────────────────────── */

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    if (typeof e.button === 'number' && e.button !== 0) return;
    if (dragged) return;

    var card = cardFor(e.target);
    if (!card) return;

    var url = urlFor(card);
    if (!url) return;

    if (isSelectingText()) return;

    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      openInNewTab(url);
      return;
    }

    window.location.href = url;
  });

  /* Middle-click behaves like middle-clicking a real link. */
  document.addEventListener('auxclick', function (e) {
    if (e.button !== 1) return;
    if (dragged) return;

    var card = cardFor(e.target);
    if (!card) return;

    var url = urlFor(card);
    if (!url) return;

    e.preventDefault();
    openInNewTab(url);
  });
})();
